import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Layers,
  X,
  SunDim,
  Moon,
  SlidersHorizontal,
  Ruler,
  Palette,
  ChevronDown,
  Star,
  ArrowRight,
  Sparkles,
  HelpCircle, // Keep Search icon if you might add a search bar later
} from "lucide-react";
import { useSelector } from "react-redux";

// --- Mock Data Import ---
// Make sure this path is correct and the data structure matches expectations
import { allProducts } from "./data";

// --- Reusable Product Card Component ---
const ProductCard = ({ product, country }) => {
  // Simplified price calculation (assuming 50% off as per banner)
  const basePrice = country === "USA" ? product.price * 0.75 : product.price; // Example conversion/base
  const salePrice = basePrice * 0.65; // Applying 40% off

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="group flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <Link
          to={`/shop/info/${product.type.replaceAll(" ", "_").toLowerCase()}`} // Link image to product info
          aria-label={`View details for ${product.name}`}
        >
          <motion.img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
          />
        </Link>
        {/* Overlay for Tags */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.bestseller && (
            <span className="flex items-center gap-1 bg-white/90 backdrop-blur-sm text-primary px-2 py-1 rounded-full text-xs font-semibold shadow-sm">
              <Star className="w-3.5 h-3.5 fill-primary" /> Bestseller
            </span>
          )}
          {product.tags?.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-1 bg-secondary/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold shadow-sm"
            >
              {tag} {/* Example: You could add icons based on tag value */}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-grow p-4 md:p-5 space-y-3">
        {/* Name and Price */}
        <div className="flex justify-between items-start gap-2">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors">
            <Link
              to={`/shop/info/${product.type
                .replaceAll(" ", "_")
                .toLowerCase()}`}
            >
              {product.name}
            </Link>
          </h3>
          <div className="text-right flex-shrink-0">
            <span className="block text-lg md:text-xl font-bold text-primary">
              ${salePrice?.toFixed(2)}{" "}
              {country === "USA" ? (
                <span className="text-sm">USD</span>
              ) : (
                <span className="text-sm">CAD</span>
              )}
            </span>
            <span className="block text-xs text-gray-500 line-through">
              ${basePrice?.toFixed(2)}{" "}
              {country === "USA" ? (
                <span className="text-xsm">USD</span>
              ) : (
                <span className="text-xsm">CAD</span>
              )}
            </span>
          </div>
        </div>

        {/* Key Features/Attributes (Scannable) */}
        <div className="text-sm text-gray-600 space-y-1.5">
          <div className="flex items-center gap-2">
            {product.lightControl === "Blackout" ? (
              <Moon className="w-4 h-4 text-gray-500" />
            ) : (
              <SunDim className="w-4 h-4 text-gray-500" />
            )}
            <span>{product.lightControl} Light Control</span>
          </div>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-500" />
            <span>{product.type} Style</span> {/* Simple type indicator */}
          </div>
        </div>

        {/* CTAs - Prioritize Customize */}
        <div className="mt-auto pt-3 space-y-2">
          <Link
            // Ensure this customize link structure is correct for your routing
            to={`/customize/details?default=${product.type
              .toLowerCase()
              .replaceAll(" ", "_")}&fabric=${product.fabric.toLowerCase()}`}
            className="flex items-center justify-center w-full px-4 py-2.5 bg-primary text-white rounded-lg font-semibold text-center shadow-sm hover:bg-primary/90 transition-all duration-200 group/cta"
            aria-label={`Customize ${product.name}`}
          >
            Customize
            <ArrowRight className="w-4 h-4 ml-2 group-hover/cta:translate-x-1 transition-transform" />
          </Link>
          <Link
            to={`/shop/info/${product.type.replaceAll(" ", "_").toLowerCase()}`}
            className="flex items-center justify-center w-full px-4 py-2 text-primary rounded-lg font-medium text-center hover:bg-primary/10 transition-all duration-200"
            aria-label={`Learn more about ${product.name}`}
          >
            View Details
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
/** @format */

// --- Main Shop Page Component ---
const ShopPage = () => {
  const location = useLocation(); // Get location object for search params
  const navigate = useNavigate(); // Get navigate function to update URL

  // --- Function to initialize state from URL ---
  const getInitialStateFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const initialFilters = {
      type: params.getAll("type") || [], // Use getAll to handle multiple values
      lightControl: params.getAll("lightControl") || [],
      roomType: params.getAll("roomType") || [],
      // Add other filters here if implemented (e.g., features)
      // features: params.getAll('feature') || [],
    };
    const initialSortBy = params.get("sort") || "featured";
    return { initialFilters, initialSortBy };
  }, [location.search]); // Depend on location.search

  // --- State Initialization ---
  const { initialFilters, initialSortBy } = getInitialStateFromURL();
  const [filters, setFilters] = useState(initialFilters);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [loading /*, setLoading */] = useState(false); // Keep loading state if data fetch needed
  const [error /*, setError */] = useState(null); // Keep error state

  const country = useSelector((state) => state.country);

  // --- Data Fetching & Processing ---
  const products = useMemo(() => allProducts, []); // Assuming static data for now

  // Filter Options (Memoized)
  const filterOptions = useMemo(
    () => ({
      type: ["Simply Cellular", "Roller", "Zebra", "Sheer"],
      lightControl: [
        "Blackout",
        "Privacy",
        "Soft Filter",
        "Transparency",
        "Solar",
      ],
      roomType: [...new Set(products.flatMap((p) => p.roomType || []))].sort(), // Safer access with || []
      // features: ["Motorized", "Cordless"]
    }),
    [products],
  );

  // Filtered & Sorted Products (Memoized)
  const filteredProducts = useMemo(() => {
    console.log("Filtering with state:", filters); // Log current filter state
    let result = products.filter(
      (product) =>
        (filters.type.length === 0 || filters.type.includes(product.type)) &&
        (filters.lightControl.length === 0 ||
          filters.lightControl.includes(product.lightControl)) &&
        (filters.roomType.length === 0 ||
          filters.roomType.some((rt) => (product.roomType || []).includes(rt))),
      // && (filters.features.length === 0 || filters.features.some(f => (product.features || []).includes(f))) // Example feature filter
    );

    // Sorting Logic (remains the same)
    result.sort((a, b) => {
      const priceA = a.price * (country === "USA" ? 0.75 : 1) * 0.5;
      const priceB = b.price * (country === "USA" ? 0.75 : 1) * 0.5;
      switch (sortBy) {
        case "price_asc":
          return priceA - priceB;
        case "price_desc":
          return priceB - priceA;
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "featured":
        default:
          const featuredA = a.bestseller || a.isFeatured ? -1 : 1;
          const featuredB = b.bestseller || b.isFeatured ? -1 : 1;
          if (featuredA !== featuredB) return featuredA - featuredB;
          return a.id - b.id;
      }
    });
    return result;
  }, [filters, products, sortBy, country]); // Dependencies

  // --- Effect to Update URL when filters or sortBy change ---
  useEffect(() => {
    console.log("State changed, updating URL params...");
    const newSearchParams = new URLSearchParams();

    // Append multi-select filters
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        values.forEach((value) => newSearchParams.append(key, value));
      }
    });

    // Append sort if not default
    if (sortBy !== "featured") {
      newSearchParams.set("sort", sortBy);
    }

    // Use navigate to update the URL search string
    // `replace: true` avoids adding duplicate history entries for filter changes
    navigate(
      { search: newSearchParams.toString() },
      { replace: true, state: location.state },
    ); // Preserve existing location state if any
  }, [filters, sortBy, navigate, location.state]); // Run when filters or sortBy change

  // --- Filter Handlers ---
  // Now these just update the state. The useEffect above handles URL sync.
  const handleMultiFilterChange = (category, value) => {
    setFilters((prev) => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [category]: newValues };
    });
  };

  const clearFilters = () => {
    setFilters({ type: [], lightControl: [], roomType: [] /*, features: []*/ });
    // Optionally reset sort as well?
    // setSortBy('featured');
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  // Calculate active filter count for UI indicators
  const activeFilterCount = Object.values(filters).reduce(
    (acc, curr) => acc + (curr?.length || 0),
    0,
  );

  // --- Render ---
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>
          Shop Custom Window Shades - Cellular, Roller, Zebra | Off Cut Shades
        </title>
        <meta
          name="description"
          content="Explore our collection of custom-made window shades. Find the perfect Cellular, Roller, Zebra, or Sheer shades with various light control options. Save up to 50%!"
        />
      </Helmet>

      {/* 1. Hero/Header Section (Keep as previously defined) */}
      <section className="pt-10 pb-8 md:pt-16 md:pb-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center md:text-left mb-6 md:mb-8">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-3 py-1 rounded-full mb-3">
              {" "}
              Site Wide Sale: Up to 35%
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {" "}
              Explore Custom Shades{" "}
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto md:mx-0">
              {" "}
              Find the perfect blend of style, privacy, and light control.
              Handcrafted in Toronto, delivered fast.{" "}
            </p>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2 text-sm font-medium">
            <Link
              to="/swatches"
              className="flex items-center gap-1.5 text-primary hover:underline"
            >
              {" "}
              <Palette className="w-4 h-4" /> Get Free Samples{" "}
            </Link>
            <Link
              to="/guides/measuring-guide"
              className="flex items-center gap-1.5 text-primary hover:underline"
            >
              {" "}
              <Ruler className="w-4 h-4" /> Measuring Guide{" "}
            </Link>
            <Link
              to="/guides/productselection"
              className="flex items-center gap-1.5 text-primary hover:underline"
            >
              {" "}
              <HelpCircle className="w-4 h-4" /> Fabric Selection Guide{" "}
            </Link>
          </div>
        </div>
      </section>

      {/* 2. Filtering and Sorting Bar (Keep structure, ensure onChange uses new handlers) */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            {/* Desktop Filters */}
            <div className="hidden md:flex flex-wrap items-center gap-2 flex-grow">
              <span className="font-medium text-sm mr-2 text-gray-600">
                Filter by:
              </span>
              {/* Type Filter */}
              <div className="relative group">
                <button
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium flex items-center gap-1 transition-colors duration-150 ${
                    filters.type.length > 0
                      ? "border-primary text-primary bg-primary/10"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                >
                  Type{" "}
                  {filters.type.length > 0 ? `(${filters.type.length})` : ""}{" "}
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </button>
                <div className="absolute top-full left-0 w-60 bg-white border border-gray-200 rounded-lg shadow-xl p-3 space-y-1 z-40 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {filterOptions.type.map((value) => (
                    <label
                      key={value}
                      className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      {" "}
                      <input
                        type="checkbox"
                        checked={filters.type.includes(value)}
                        onChange={() => handleMultiFilterChange("type", value)}
                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50 focus:ring-offset-0 focus:ring-1"
                      />{" "}
                      <span className="text-sm text-gray-800">
                        {value}
                      </span>{" "}
                    </label>
                  ))}
                </div>
              </div>
              {/* Light Control Filter */}
              <div className="relative group">
                <button
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium flex items-center gap-1 transition-colors duration-150 ${
                    filters.lightControl.length > 0
                      ? "border-primary text-primary bg-primary/10"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                >
                  Light Control{" "}
                  {filters.lightControl.length > 0
                    ? `(${filters.lightControl.length})`
                    : ""}{" "}
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </button>
                <div className="absolute top-full left-0 w-60 bg-white border border-gray-200 rounded-lg shadow-xl p-3 space-y-1 z-40 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {filterOptions.lightControl.map((value) => (
                    <label
                      key={value}
                      className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      {" "}
                      <input
                        type="checkbox"
                        checked={filters.lightControl.includes(value)}
                        onChange={() =>
                          handleMultiFilterChange("lightControl", value)
                        }
                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50 focus:ring-offset-0 focus:ring-1"
                      />{" "}
                      <span className="text-sm text-gray-800">
                        {value}
                      </span>{" "}
                    </label>
                  ))}
                </div>
              </div>
              {/* Room Type Filter */}
              <div className="relative group">
                <button
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium flex items-center gap-1 transition-colors duration-150 ${
                    filters.roomType.length > 0
                      ? "border-primary text-primary bg-primary/10"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                  }`}
                >
                  Room Type{" "}
                  {filters.roomType.length > 0
                    ? `(${filters.roomType.length})`
                    : ""}{" "}
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </button>
                <div className="absolute top-full left-0 w-60 bg-white border border-gray-200 rounded-lg shadow-xl p-3 space-y-1 z-40 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 max-h-60 overflow-y-auto">
                  {filterOptions.roomType.map((value) => (
                    <label
                      key={value}
                      className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      {" "}
                      <input
                        type="checkbox"
                        checked={filters.roomType.includes(value)}
                        onChange={() =>
                          handleMultiFilterChange("roomType", value)
                        }
                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50 focus:ring-offset-0 focus:ring-1"
                      />{" "}
                      <span className="text-sm text-gray-800">
                        {value}
                      </span>{" "}
                    </label>
                  ))}
                </div>
              </div>
              {/* Clear Filters Button */}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-gray-500 hover:text-primary hover:underline ml-2 transition-colors duration-150"
                  title="Clear all active filters"
                >
                  {" "}
                  Clear All ({activeFilterCount}){" "}
                </button>
              )}
            </div>

            {/* Mobile Filters Button */}
            <div className="w-full flex md:hidden items-center justify-between">
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                aria-label="Open filters menu"
              >
                {" "}
                <SlidersHorizontal className="w-4 h-4" /> Filters{" "}
                {activeFilterCount > 0 && `(${activeFilterCount})`}{" "}
              </button>
              {/* Sorting Dropdown (Mobile) */}
              <div className="flex items-center gap-2">
                {" "}
                <label htmlFor="sort-by-mobile" className="sr-only">
                  Sort by:
                </label>{" "}
                <select
                  id="sort-by-mobile"
                  value={sortBy}
                  onChange={handleSortChange}
                  className="text-sm font-medium border-gray-300 rounded-lg focus:ring-primary/50 focus:border-primary pr-8"
                  aria-label="Sort products"
                >
                  {" "}
                  <option value="featured">Featured</option>{" "}
                  <option value="price_asc">Price: Low to High</option>{" "}
                  <option value="price_desc">Price: High to Low</option>{" "}
                  <option value="name_asc">Name: A to Z</option>{" "}
                </select>{" "}
              </div>
            </div>

            {/* Desktop Sorting */}
            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              <label
                htmlFor="sort-by-desktop"
                className="text-sm font-medium text-gray-600"
              >
                Sort by:
              </label>
              <select
                id="sort-by-desktop"
                value={sortBy}
                onChange={handleSortChange}
                className="text-sm font-medium border-gray-300 rounded-lg focus:ring-primary/50 focus:border-primary pr-8"
                aria-label="Sort products"
              >
                {" "}
                <option value="featured">Featured</option>{" "}
                <option value="price_asc">Price: Low to High</option>{" "}
                <option value="price_desc">Price: High to Low</option>{" "}
                <option value="name_asc">Name: A to Z</option>{" "}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Products Grid (Keep structure, ensure ProductCard is imported) */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-sm text-gray-600">
            Showing {filteredProducts.length} of {products.length} products
            {activeFilterCount > 0 && ` matching your filters`}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            <AnimatePresence>
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  country={country}
                />
              ))}
            </AnimatePresence>
          </div>
          {/* No Results State */}
          <AnimatePresence>
            {filteredProducts.length === 0 && activeFilterCount > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center py-20 md:py-32"
              >
                <Sparkles className="w-12 h-12 text-primary/40 mx-auto mb-4" />{" "}
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3">
                  {" "}
                  No Shades Match Your Filters{" "}
                </h3>{" "}
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {" "}
                  Try adjusting your selections or clear the filters to see all
                  available products.{" "}
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-6 py-2.5 bg-primary text-white rounded-lg font-medium shadow-sm hover:bg-primary/90 transition-colors group"
                >
                  {" "}
                  Clear All Filters{" "}
                  <X className="w-4 h-4 ml-2 group-hover:rotate-90 transition-transform" />{" "}
                </button>
              </motion.div>
            )}
            {/* Optional: Handle case where products array itself is empty */}
            {products.length === 0 && !loading && (
              <motion.div /* ... No products available message ... */>
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3">
                  No Products Available
                </h3>{" "}
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Please check back later or contact us.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* --- Mobile Filter Sidebar (Ensure onChange uses new handlers) --- */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              aria-hidden="true"
            />
            {/* Sidebar */}
            <motion.div
              key="filters-sidebar"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
              className="fixed top-0 left-0 bottom-0 w-4/5 max-w-sm bg-white z-50 shadow-xl flex flex-col md:hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-filters-heading"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                {" "}
                <h2
                  id="mobile-filters-heading"
                  className="text-lg font-semibold"
                >
                  Filters
                </h2>{" "}
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-500 hover:text-gray-800 p-1"
                  aria-label="Close filters menu"
                >
                  {" "}
                  <X className="w-6 h-6" />{" "}
                </button>{" "}
              </div>
              <div className="flex-grow p-4 space-y-6 overflow-y-auto">
                {/* Type Filters */}
                <fieldset>
                  {" "}
                  <legend className="font-semibold mb-2">
                    Shade Type
                  </legend>{" "}
                  <div className="space-y-1.5">
                    {" "}
                    {filterOptions.type.map((value) => (
                      <label
                        key={value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {" "}
                        <input
                          type="checkbox"
                          checked={filters.type.includes(value)}
                          onChange={() =>
                            handleMultiFilterChange("type", value)
                          }
                          className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50"
                        />{" "}
                        <span>{value}</span>{" "}
                      </label>
                    ))}{" "}
                  </div>{" "}
                </fieldset>
                {/* Light Control Filters */}
                <fieldset>
                  {" "}
                  <legend className="font-semibold mb-2">
                    Light Control
                  </legend>{" "}
                  <div className="space-y-1.5">
                    {" "}
                    {filterOptions.lightControl.map((value) => (
                      <label
                        key={value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {" "}
                        <input
                          type="checkbox"
                          checked={filters.lightControl.includes(value)}
                          onChange={() =>
                            handleMultiFilterChange("lightControl", value)
                          }
                          className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50"
                        />{" "}
                        <span>{value}</span>{" "}
                      </label>
                    ))}{" "}
                  </div>{" "}
                </fieldset>
                {/* Room Type Filters */}
                <fieldset>
                  {" "}
                  <legend className="font-semibold mb-2">Room Type</legend>{" "}
                  <div className="space-y-1.5">
                    {" "}
                    {filterOptions.roomType.map((value) => (
                      <label
                        key={value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {" "}
                        <input
                          type="checkbox"
                          checked={filters.roomType.includes(value)}
                          onChange={() =>
                            handleMultiFilterChange("roomType", value)
                          }
                          className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary/50"
                        />{" "}
                        <span>{value}</span>{" "}
                      </label>
                    ))}{" "}
                  </div>{" "}
                </fieldset>
              </div>
              {/* Footer Buttons */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={activeFilterCount === 0}
                  aria-label="Clear all filters"
                >
                  {" "}
                  Clear All{" "}
                  {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}{" "}
                </button>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="px-6 py-2 bg-primary text-white rounded-lg text-sm font-semibold shadow-sm hover:bg-primary/90"
                  aria-label="Apply filters and close menu"
                >
                  {" "}
                  View Results{" "}
                </button>{" "}
                {/* Changed text */}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShopPage;
